/**
 * Brazil timezone utilities (America/Sao_Paulo — UTC-3 / UTC-2 DST).
 * All date-boundary logic in the app should use these helpers
 * so "today" always means "today in Brazil".
 */

const BRAZIL_TZ = 'America/Sao_Paulo';

/**
 * Returns the current Date object representing "now" in Brazil.
 * Note: The returned Date is still a JS Date (internally UTC),
 * but its value is shifted so `.getHours()` etc. return Brazil time.
 */
export function nowBrazil(): Date {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: BRAZIL_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date());
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '0';
  return new Date(
    +get('year'),
    +get('month') - 1,
    +get('day'),
    +get('hour'),
    +get('minute'),
    +get('second'),
  );
}

/**
 * Returns today's date in Brazil as `YYYY-MM-DD`.
 */
export function todayBrazilISO(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BRAZIL_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/**
 * Returns current date in Brazil as DD/MM/YYYY.
 */
export function formatDateBrazil(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: BRAZIL_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/**
 * Returns the UTC offset (in ms) for Brazil at a given probe date.
 * Positive = UTC is ahead of Brazil (i.e. Brazil is behind UTC).
 */
function getBrazilOffsetMs(probe: Date): number {
  const utcStr = probe.toLocaleString('en-US', { timeZone: 'UTC' });
  const brazilStr = probe.toLocaleString('en-US', { timeZone: BRAZIL_TZ });
  return new Date(utcStr).getTime() - new Date(brazilStr).getTime();
}

/**
 * Returns the ISO timestamp string for the start of today in Brazil.
 * E.g. if it's 2026-04-03 in Brazil, returns "2026-04-03T03:00:00.000Z" (BRT)
 * or "2026-04-03T02:00:00.000Z" during DST.
 */
export function startOfDayBrazilISO(): string {
  const today = todayBrazilISO(); // YYYY-MM-DD in Brazil
  const probe = new Date(`${today}T12:00:00Z`); // safe mid-day probe for offset
  const offsetMs = getBrazilOffsetMs(probe);
  return new Date(new Date(`${today}T00:00:00Z`).getTime() + offsetMs).toISOString();
}

/**
 * Returns the current hour in Brazil (0-23).
 */
export function currentHourBrazil(): number {
  return nowBrazil().getHours();
}

/**
 * Returns first day of current month in Brazil as YYYY-MM-DD.
 */
export function firstDayOfMonthBrazil(): string {
  const n = nowBrazil();
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}

/**
 * Returns last day of current month in Brazil as YYYY-MM-DD.
 */
export function lastDayOfMonthBrazil(): string {
  const n = nowBrazil();
  const last = new Date(n.getFullYear(), n.getMonth() + 1, 0);
  const y = last.getFullYear();
  const m = String(last.getMonth() + 1).padStart(2, '0');
  const d = String(last.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Returns { month, year } based on current Brazil time.
 * month is 1-indexed (1 = January).
 */
export function currentMonthYearBrazil(): { month: number; year: number } {
  const n = nowBrazil();
  return { month: n.getMonth() + 1, year: n.getFullYear() };
}

/**
 * Returns a YYYY-MM-DD string for N days ago in Brazil time.
 */
export function daysAgoBrazilISO(days: number): string {
  const n = nowBrazil();
  n.setDate(n.getDate() - days);
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, '0');
  const d = String(n.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Returns current time in Brazil as HH:mm:ss.
 */
export function formatTimeBrazil(): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: BRAZIL_TZ,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date());
}

/**
 * Returns current month label in Brazil, e.g. "abr/26", "jan/27".
 */
export function formatMonthLabelBrazil(): string {
  const n = nowBrazil();
  const month = n.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toLowerCase();
  const year = n.getFullYear().toString().slice(-2);
  return `${month}/${year}`;
}

/**
 * Converts a Brazil date + time (from datetime-local inputs) to UTC ISO string.
 * E.g. brazilDateTimeToISO('2026-04-03', '14:30') → '2026-04-03T17:30:00.000Z' (BRT)
 */
export function brazilDateTimeToISO(date: string, time: string): string {
  const dtStr = `${date}T${time.length === 5 ? time + ':00' : time}`;
  const probe = new Date(`${date}T12:00:00Z`);
  const offsetMs = getBrazilOffsetMs(probe);
  return new Date(new Date(`${dtStr}Z`).getTime() + offsetMs).toISOString();
}

/**
 * Converts a UTC ISO string to a `YYYY-MM-DDTHH:mm` string in Brazil timezone.
 * Used to populate `datetime-local` inputs showing Brazil time.
 */
export function isoToBrazilDateTimeLocal(iso: string): string {
  const d = new Date(iso);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}
